<script lang="ts" setup>
import { vStencilReady } from '@dh-io-owpi/shared/src/directives/stencil-ready.directive';
import { ref } from 'vue';
import type { PiSpaHighlight } from '../SpaHighlight';
import { formatLabel, useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import { useVideoTrackerStore } from '@/stores/videoTracker';

defineProps<{
  theme: 'dark' | 'light';
  items: PiSpaHighlight[];
}>();

const sliderEl = ref<HTMLElement>();
const aspectRatioEls = ref<HTMLElement[]>();
const { createTrackOnceFn, track } = useTrackingStore();
const { trackVideoProgress } = useVideoTrackerStore();

const setNavBtnOffset = () => {
  const offset = aspectRatioEls.value![0].clientHeight / 2;
  sliderEl.value!.style.setProperty('--arrow-offset-top', `${offset}px`);
};

const getImageUrl = (publishUrl: string) => {
  const url = new URL(publishUrl);
  return url.pathname;
};

const trackShowMore = createTrackOnceFn({
  category: 'feature',
  action: 'usp|highlights',
  label: 'see_more_cards',
});

const trackTimeout = 500;

const videoPlayHandler = (item: PiSpaHighlight) => {
  track({
    timeout: trackTimeout,
    name: 'video_start',
    label: formatLabel(item.title),
    action: 'usp|video_html5.video_start|highlights',
    category: 'video',
  });
};

const videoPauseHandler = (item: PiSpaHighlight, $event: Event) => {
  const target = $event.currentTarget as HTMLVideoElement;

  // Avoid tracking pause event when video ends
  if (target.currentTime === target.duration) {
    return;
  }
  track({
    timeout: trackTimeout,
    label: formatLabel(item.title),
    action: 'usp|video_html5.video_pause|highlights',
    category: 'video',
  });
};
</script>

<template>
  <wb-slider
    ref="sliderEl"
    v-stencil-ready="setNavBtnOffset"
    :theme="theme"
    off-grid
    grid-gutter
    scroll-snap
    show-dot-navigation
    show-arrow-navigation
    class="owpi-spa-usp-slider"
    @wbchange="trackShowMore"
  >
    <div
      v-for="item in items"
      :key="item.modelSeries + item.title"
      class="wb-slider__item wb-grid-col-mq1-11 wb-grid-col-mq3-5 owpi-spa-usp-slider__item"
    >
      <wb-aspect-ratio ref="aspectRatioEls" ratio="16x9">
        <video
          v-if="item.image.type === 'multimedia'"
          preload="metadata"
          controls
          @play="videoPlayHandler(item)"
          @pause="videoPauseHandler(item, $event)"
          @timeupdate="
            trackVideoProgress({
              id: item.modelSeries + item.title,
              videoTitle: item.title,
              sectionName: 'usp',
              componentName: 'highlights',
              event: $event,
            })
          "
        >
          <source :src="`${getImageUrl(item.image._publishUrl)}#t=0.1`" :type="item.image.format" />
        </video>
        <img v-else :src="getImageUrl(item.image._publishUrl)" :alt="item.title" />
      </wb-aspect-ratio>

      <p class="wb-type-heading-s owpi-spa-usp-slider__title">{{ item.title }}</p>
      <p class="wb-type-copy owpi-spa-usp-slider__description" v-html="item.description.html" />
    </div>
  </wb-slider>
</template>

<style lang="scss">
.owpi-spa-usp-slider {
  z-index: 0; // For some weird reason safari needs this, otherwise slider will be on top of the salesbar momentarily when sliding trough usp items
  &__title {
    margin-top: var(--wb-spacing-xs);
  }

  &__description {
    margin-top: var(--wb-spacing-xxs);
  }

  @include theme(dark) {
    & p {
      color: var(--wb-white);
    }
  }
}

.owpi-spa-usp-slider .wb-slider__arrow {
  top: var(--arrow-offset-top) !important;
}
</style>
